import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useProfile } from "./ProfileContext";

/** localStorage key prefix for the rail's open/closed state, scoped per business. */
const OPEN_STORAGE_PREFIX = "graftus_coachRailOpen_";

export interface CoachRailContextValue {
  /** Whether the rail is expanded (452px) rather than the 44px closed edge. Persisted per user. */
  open: boolean;
  openRail: () => void;
  closeRail: () => void;
  toggleRail: () => void;

  /** The thread currently shown in the rail. `null` is a fresh, unsaved thread. */
  activeThreadId: string | null;
  selectThread: (id: string | null) => void;
  /** Clear the rail to a fresh thread (keeps the rail open). */
  newThread: () => void;

  /**
   * Bumped only by `newThread`/`openWith` — an explicit "start over" signal the
   * rail keys its chat on to force a fresh transcript. Selecting a saved thread
   * deliberately does NOT bump it, so the chat hydrates in place without a
   * remount flash.
   */
  resetNonce: number;

  /** The thread-title dropdown / picker popover state. */
  threadPickerOpen: boolean;
  setThreadPickerOpen: (open: boolean) => void;

  /** The ⌘K command palette / inline ask (2b) — the AI's front door. */
  paletteOpen: boolean;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;

  /**
   * User-chosen scope/topic for the *next* new thread, mirroring the `/coach`
   * URL params (`scope` = "org" | "team:<id>" | "member:<id>"). Once a thread is
   * active its own scope/topic win, so these are cleared on `newThread`.
   */
  scopeParam: string | null;
  setScopeParam: (param: string | null) => void;
  topicParam: string | null;
  setTopicParam: (param: string | null) => void;

  /**
   * A message queued to auto-send when the rail next renders an empty thread —
   * how "ask anywhere" (2b) and inline "Ask …" hand a question to the coach.
   * The nonce lets the rail re-consume the same text.
   */
  pendingMessage: { text: string; nonce: number } | null;
  /** Open the rail on a fresh thread and queue `text` to send. */
  openWith: (text: string, opts?: { scopeParam?: string | null }) => void;
  /** Clear the queued message once the rail has consumed it. */
  clearPendingMessage: () => void;
}

const CoachRailContext = createContext<CoachRailContextValue | undefined>(
  undefined,
);

/**
 * Holds the coach rail's cross-route state (4a/4b). The rail is rendered once in
 * `AppLayout` and reads this context, so its thread, scope and open/closed state
 * survive navigating Today → Candidates → Playbook. `open` is persisted per
 * business; everything else is session state.
 */
export function CoachRailProvider({ children }: { children: ReactNode }) {
  const { businessProfile } = useProfile();
  const businessId = businessProfile?.uid ?? "";

  const [open, setOpen] = useState(false);
  const [hydratedBusinessId, setHydratedBusinessId] = useState<string | null>(
    null,
  );
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [threadPickerOpen, setThreadPickerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [scopeParam, setScopeParam] = useState<string | null>(null);
  const [topicParam, setTopicParam] = useState<string | null>(null);
  const [resetNonce, setResetNonce] = useState(0);
  const [pendingMessage, setPendingMessage] = useState<{
    text: string;
    nonce: number;
  } | null>(null);
  const nonceRef = useRef(0);

  // Hydrate the persisted open state once per business (mirrors TeamScopeContext).
  useEffect(() => {
    if (!businessId) {
      setOpen(false);
      setHydratedBusinessId(null);
      return;
    }
    if (hydratedBusinessId === businessId) return;
    const stored = localStorage.getItem(OPEN_STORAGE_PREFIX + businessId);
    setOpen(stored === "1");
    setHydratedBusinessId(businessId);
  }, [businessId, hydratedBusinessId]);

  const persistOpen = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!businessId) return;
      localStorage.setItem(OPEN_STORAGE_PREFIX + businessId, next ? "1" : "0");
    },
    [businessId],
  );

  const openRail = useCallback(() => persistOpen(true), [persistOpen]);
  const closeRail = useCallback(() => {
    persistOpen(false);
    setThreadPickerOpen(false);
  }, [persistOpen]);
  const toggleRail = useCallback(() => persistOpen(!open), [persistOpen, open]);

  const selectThread = useCallback((id: string | null) => {
    setActiveThreadId(id);
    setThreadPickerOpen(false);
  }, []);

  const newThread = useCallback(() => {
    setActiveThreadId(null);
    setScopeParam(null);
    setTopicParam(null);
    setThreadPickerOpen(false);
    setPendingMessage(null);
    setResetNonce((n) => n + 1);
  }, []);

  const openWith = useCallback(
    (text: string, opts?: { scopeParam?: string | null }) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // A fresh thread so the queued message is the first turn.
      setActiveThreadId(null);
      setTopicParam(null);
      if (opts && "scopeParam" in opts) setScopeParam(opts.scopeParam ?? null);
      else setScopeParam(null);
      setThreadPickerOpen(false);
      nonceRef.current += 1;
      setPendingMessage({ text: trimmed, nonce: nonceRef.current });
      setResetNonce((n) => n + 1);
      persistOpen(true);
    },
    [persistOpen],
  );

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const togglePalette = useCallback(() => setPaletteOpen((o) => !o), []);

  const clearPendingMessage = useCallback(() => setPendingMessage(null), []);

  const value = useMemo<CoachRailContextValue>(
    () => ({
      open,
      openRail,
      closeRail,
      toggleRail,
      resetNonce,
      activeThreadId,
      selectThread,
      newThread,
      threadPickerOpen,
      setThreadPickerOpen,
      paletteOpen,
      openPalette,
      closePalette,
      togglePalette,
      scopeParam,
      setScopeParam,
      topicParam,
      setTopicParam,
      pendingMessage,
      openWith,
      clearPendingMessage,
    }),
    [
      open,
      openRail,
      closeRail,
      toggleRail,
      activeThreadId,
      selectThread,
      newThread,
      resetNonce,
      threadPickerOpen,
      paletteOpen,
      openPalette,
      closePalette,
      togglePalette,
      scopeParam,
      topicParam,
      pendingMessage,
      openWith,
      clearPendingMessage,
    ],
  );

  return (
    <CoachRailContext.Provider value={value}>
      {children}
    </CoachRailContext.Provider>
  );
}

export function useCoachRail(): CoachRailContextValue {
  const ctx = useContext(CoachRailContext);
  if (!ctx) {
    throw new Error("useCoachRail must be used within a CoachRailProvider");
  }
  return ctx;
}
